import React, { useEffect, useRef } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { SignIn, useAuth, useUser } from "@clerk/clerk-react";
import { useSelector, useDispatch } from "react-redux";
import Layout from "../components/Layout";
import Page404 from "../pages/404";
import HistoryTable from "../components/ExportHistoryScreen";
import ProviderDetails from "../pages/providerDetails";
import MyDocuments from "../pages/MyDocuments";
import CenteredContainer from "../components/CenteredContainer";
import Discovery from "../pages/discovery";
import { useUserRole, isBackendViewOnlyUser } from "../utils/helperMethods";
import { useLoggedInGetSponsorUserDetails } from "../custom-hooks/queries";
import {
  resolveSponsorAccess,
  isTrialExperienceOrgEnabled,
} from "../utils/sponsorAccessContext";
import { ComingSoon } from "../pages/comingSoon";
import { DocumentLibrary } from "../pages/DocumentLibrary";
import { VqaLibrary } from "../pages/VqaLibrary";
import { BackendUserAssessment } from "../pages/BackendUser/BackendAssesment";
import { RFILibrary } from "../pages/RFILibrary";
import { ProviderVQAGrid } from "../pages/ProviderUser/VQAGrid";
import { SponsorVQAGrid } from "../pages/SponsorVQARequestGrid";
import { RequestNewAssessment } from "../pages/RequestNewAssessment";
import { SponsorAssessmentGrid } from "../pages/SponsorAssessment";
import { ProviderAssessmentGrid } from "../pages/ProviderUser/AssessmentGrid";
import { AdminAssessmentGrid } from "../pages/BackendUser/BackendAssessmentsGrid";
import { AssessmentDetailPage } from "../pages/AssessmentDetailPage";
import { BackendManageAssessment } from "../pages/BackendUser/ManageAssessments";
import { SponsorDetailAssessment } from "../pages/SponsorAssessmentDetailPage";
import AuditorDetail from "../pages/AuditorDetails";
import { AdminAssessmentRequestGrid } from "../pages/BackendUser/BackendAssessmentRequestGrid";
import { BackendRequestDetailPage } from "../pages/BackendUser/BackendRequestDetailPage";
import { Poc } from "../pages/Poc";
import UsersList from "../pages/BackendUser/Users/Users";
import AssessmentCalendar from "../pages/AssessmentCalendar";
import CreateVendorAccountPage from "../pages/BackendUser/Users/CreateVendorAccountPage";
import AddVendorAccount from "../pages/BackendUser/Users/AddVendorAccountForm";
import AddSponsorAccount from "../pages/BackendUser/Users/AddNewSponserForm";
import SponsorsUsersList from "../pages/BackendUser/Users/sponsers";
import BackendUsersList from "../pages/BackendUser/Users/BackendUser";
import AddBackendUserAccount from "../pages/BackendUser/Users/AddBackendUser";
import ManageParticipantsList from "../pages/BackendUser/ManageParticipantsList";
import JoinRequestsQueue from "../pages/BackendUser/JoinRequestsQueue";
import { JoinvqaDetailAssessment } from "../pages/AssesmentParticipation/JoinVqaDetails";
import { JoinvqaRequestDetailAssessment } from "../pages/AssesmentParticipation/JoinVqaRequestDetails";
import { RequestAssessmentListGrid } from "../pages/AssesmentParticipation/AssesmentRequestList";
import { AssesmentRequestDetails } from "../pages/AssesmentParticipation/AssesmentRequestDetailPage";
import PocChatBot from "../pages/POC/PocChatBot";
import RFIUploadPage from "../pages/POC/RFIUploadPage";
import ManageBackendUser from "../pages/BackendUser/Users/ManageBackendUser";
import ManageSponsor from "../pages/BackendUser/Users/ManageSponsor";
import MyProfile from "../pages/BackendUser/Users/MyProfile";
import SponsorMasterUserList from "../pages/BackendUser/Users/SponsorMasterUser";
import AddSponsorMasterUser from "../pages/BackendUser/Users/AddSponsorMasterUser";
import ManageSponsorMasterUser from "../pages/BackendUser/Users/ManageSponsorMasterUser";
import ManageSponsorUser from "../pages/SponsorUser/Users/Me";
import ManageVendorAccount from "../pages/BackendUser/Users/ManageVendorAccountForm";
import SponsorUserGrid from "../pages/BackendUser/Users/SponsorUserGrid";
import Providers from "../pages/BackendUser/Users/Providers";
import AddProviderUser from "../pages/BackendUser/Users/AddProviderUser";
import SponsorForm from "../components/SponsorFrom";
import ResetPasswordPage from "../components/ForgotPasswordPage";
import AddSponsorUsers from "../pages/BackendUser/Users/AddSponsorUsers";
import ManageTeam from "../pages/Sponsor/Team/ManageTeam";
import AddTeamMember from "../pages/Sponsor/Team/AddTeamMember";
import { MyRFIs } from "../pages/RFIS/MyRfis";
import RFIDetails from "../pages/RFIS/RFIDetails";
import { RequestRFis } from "../pages/RFIS/RequestRFis";
import { NewRequestVendor } from "../pages/NewRequestVendor";
import { BackendRfis } from "../pages/BackendUser/RFIs/BackendRfis";
import AdminRiskModels from "../pages/BackendUser/AdminRiskModels";
import AdminDiligentRiskModel from "../pages/BackendUser/AdminDiligentRiskModel";
import StatusDashboard from "../pages/StatusDashboard/StatusDashboard";
import ProviderStatusDashboard from "../pages/ProviderUser/Dashboard/ProviderStatusDashboard";
import { ProviderRequestedRfis } from "../pages/ProviderUser/RequestRfis/ProviderRequestedRfis";
import { KnowledgeBase } from "../pages/ProviderUser/Knowledgebase/KnowledgeBase";
import HypercomplyCallback from "../pages/RFIS/HypercomplyCallback";
import SSOBridge from "../pages/RFIS/SSOBridge";
import { RFITemplateBuilder } from "../pages/RFIS/RFITemplateBuilder";
import { Documents } from "../pages/ProviderUser/Documents/Documents";
import { Segmentation } from "../pages/ProviderUser/Segmentation/Segmentation";
import { TermsAndConditionsPage } from "../pages/ProviderUser/TermsAndConditionsPage";
import {
  useGetFirstTimeSignIn,
  useGetVendorsMe,
} from "../custom-hooks/queries";
import ManageProviderUser from "../pages/ProviderUser/Me";
import { DataRooms } from "../pages/ProviderUser/DataRooms/DataRooms";
import { TrustPages } from "../pages/ProviderUser/TrustPages/TrustPages";
import { Questionnaire } from "../pages/ProviderUser/Questionnaire/Questionnaire";
import ClinicalDashboard from "../pages/SponsorUser/ClinicalDashboard";
import { Vendors } from "../pages/ProviderUser/Vendor/Vendor";
import MyVendorsPage, {
  MyVendorsLegacyRouteRedirect,
} from "../pages/MyVendors/MyVendorsPage";
import VendorListDetailPage from "../pages/MyVendors/VendorListDetailPage";
import SponsorRiskModels from "../pages/Sponsor/SponsorRiskModels";
import { NEW_MY_VENDORS_UI_ENABLED } from "../utils/myVendorsFilterOptions";
import LoadingModal from "../components/Skeletons/LoadingModal";
import MfaSetupPage from "../pages/mfa/setup";
import { MfaEnforcedRoute } from "../components/MfaEnforcedRoute";
import {
  selectUIFreeze,
  startRoleSwitch,
  endRoleSwitch,
} from "../redux-store/features/uiSlice";
import VendorTeamManagement from "../pages/BackendUser/Users/VendorTeamManagement";
import LibraryRequestsEmailLinkRedirect from "../pages/LibraryRequestsEmailLinkRedirect";
import { LIBRARY_REQUESTS_EMAIL_PATHS } from "../utils/libraryRequestsEmailLinkPaths";

/**
 * ProtectedRoute - Smart redirection with loading state handling
 *
 * Key improvements:
 * - Shows loading modal while permissions are being loaded (first 300ms)
 * - Never redirects while rights/roles are temporarily empty during role switch
 * - Uses stable role from Redux to prevent flicker
 */
const ProtectedRoute = ({
  children,
  requiredRights = [],
  requiredRoles = [],
  allowBackendAdminViewAccess = false,
  allowMyVendorsViewAccess = false,
  blockForAdminUsers = false,
}) => {
  const { user, isLoaded: isUserLoaded } = useUser();
  const stableRole = useUserRole();
  const loggedInUser = useSelector((state) => state.loggedInUser);
  const reduxRights = useSelector((state) => state.userRights?.rights) || [];
  const clerkMetadata = user?.publicMetadata || {};
  const navigate = useNavigate();
  const freeze = useSelector(selectUIFreeze);
  const permissionsLoadedRef = useRef(false);
  const [isPermissionsReady, setIsPermissionsReady] = React.useState(false);

  const isSponsorUser =
    stableRole === "Sponsor User" || stableRole === "Sponsor Master User";
  const { data: sponsorProfile, isLoading: sponsorProfileLoading } =
    useLoggedInGetSponsorUserDetails(stableRole);

  const apiProfile =
    loggedInUser?.userDetails?.rights?.length > 0
      ? loggedInUser.userDetails
      : sponsorProfile;

  const { rights: userRights, subRoles: userSubRoles, role: userRole } =
    resolveSponsorAccess({
      clerkMetadata,
      sponsorProfile: apiProfile,
      reduxRights,
    });

  const isBackendAdminView =
    allowBackendAdminViewAccess &&
    (userRole === "Backend User" || userRole === "Backend Master User");
  const isBackendOnlyViewOnly = isBackendViewOnlyUser(userRole);

  const waitingForSponsorProfile =
    isSponsorUser &&
    sponsorProfileLoading &&
    userRights.length === 0 &&
    userSubRoles.length === 0;

  // Track when permissions are loaded (with 300ms grace period)
  useEffect(() => {
    if (!isUserLoaded) return;

    if (waitingForSponsorProfile) {
      setIsPermissionsReady(false);
      return;
    }

    if (userRights.length > 0 || userSubRoles.length > 0) {
      permissionsLoadedRef.current = true;
      setIsPermissionsReady(true);
      return;
    }

    const timer = setTimeout(() => {
      permissionsLoadedRef.current = true;
      setIsPermissionsReady(true);
    }, 300);

    return () => clearTimeout(timer);
  }, [
    isUserLoaded,
    userRights.length,
    userSubRoles.length,
    waitingForSponsorProfile,
  ]);

  // Show loader during freeze or while permissions are loading
  if (freeze || !isPermissionsReady) {
    return <LoadingModal open={true} />;
  }

  // Block only Backend User / Backend Master User from request/create routes (Sponsor Admin can make requests)
  if (blockForAdminUsers && isBackendOnlyViewOnly) {
    return <Navigate to="/" replace />;
  }

  // Allow Backend User / Backend Master User view-only access to Discovery, Doc Library, Assessment Calendar
  if (isBackendAdminView) {
    return children;
  }

  // My Vendors surfaces platform RFI rows — viewers with that right must reach RFI Details
  if (allowMyVendorsViewAccess && userRights.includes("My Vendors")) {
    return children;
  }

  const hasRequiredRights =
    requiredRights.length === 0 ||
    requiredRights.some((right) => {
      if (right === "Trial Experience Dashboard") {
        return (
          isTrialExperienceOrgEnabled(loggedInUser, apiProfile, clerkMetadata) &&
          userRights.includes(right)
        );
      }
      return userRights.includes(right);
    });

  // Check if user has any of the required roles
  const hasRequiredRoles =
    requiredRoles.length === 0 ||
    requiredRoles.some((role) => userSubRoles.includes(role));

  // If user doesn't have required permissions, implement smart redirection
  if (!hasRequiredRights || !hasRequiredRoles) {
    // Check what other access rights the user has and redirect accordingly

    // For routes that require "Vendor Discovery" (home page)
    if (requiredRights.includes("Vendor Discovery")) {
      // First check if user has Document Library access
      if (userRights.includes("Document Library")) {
        return <Navigate to="/Documents" replace />;
      }

      // Next, check role-based redirects for assessment or RFI access
      const isVQAUser =
        userSubRoles.includes("VQA Manager") ||
        userSubRoles.includes("Sponsor Admin");
      const isRFIUser =
        userSubRoles.includes("RFI Manager") ||
        userSubRoles.includes("Sponsor Admin");

      if (isVQAUser && userRights.includes("View My Assessment")) {
        return <Navigate to="/Assessment/History" replace />;
      }

      if (isRFIUser && userRights.includes("View Released RFIs")) {
        return <Navigate to="/documents/RFI" replace />;
      }

      // If no specific rights match, default redirect
      return <Navigate to="/" replace />;
    }

    // For other protected routes without specific redirects
    return <Navigate to="/" replace />;
  }

  // User has the required permissions
  return children;
};

/**
 * ProtectedRouteVendor - Protected route for vendor users with loading state handling
 *
 * Key improvements:
 * - Shows loading modal while vendor data is loading
 * - Never redirects during first 300ms of metadata refresh
 * - Prevents blank screen during role switch
 */
const ProtectedRouteVendor = ({
  children,
  requiredRights = [],
  requiredRoles = [],
}) => {
  const userRole = useUserRole();
  const cachedVendor = useSelector(
    (state) => state.vendorUserDetails?.userDetails
  );
  const { data: vendorUser, isLoading } = useGetVendorsMe(userRole);
  const navigate = useNavigate();
  const freeze = useSelector(selectUIFreeze);
  const permissionsLoadedRef = useRef(false);
  const [isPermissionsReady, setIsPermissionsReady] = React.useState(false);

  const effectiveVendor =
    vendorUser?.provider_user_permissions?.length > 0
      ? vendorUser
      : cachedVendor;

  const userRights =
    effectiveVendor?.provider_user_permissions?.map(
      (right) => right?.permission
    ) || [];

  const userSubRoles =
    effectiveVendor?.provider_user_subrole?.map((role) => role?.subrole) || [];

  // Track when permissions are loaded (with 300ms grace period)
  useEffect(() => {
    if (isLoading && userRights.length === 0 && userSubRoles.length === 0) return;

    if (userRights.length > 0 || userSubRoles.length > 0) {
      permissionsLoadedRef.current = true;
      setIsPermissionsReady(true);
      return;
    }

    const timer = setTimeout(() => {
      permissionsLoadedRef.current = true;
      setIsPermissionsReady(true);
    }, 300);

    return () => clearTimeout(timer);
  }, [isLoading, userRights.length, userSubRoles.length]);

  // Show loader during freeze, loading, or while permissions are loading
  if (freeze || isLoading || !isPermissionsReady) {
    return <LoadingModal open={true} />;
  }

  // Check if user has any of the required rights
  const hasRequiredRights =
    requiredRights.length === 0 ||
    requiredRights.some((right) => userRights.includes(right));

  // Check if user has any of the required roles
  const hasRequiredRoles =
    requiredRoles.length === 0 ||
    requiredRoles.some((role) => userSubRoles.includes(role));

  // If user doesn't have required permissions, implement smart redirection
  if (!hasRequiredRights || !hasRequiredRoles) {
    // For vendor users, implement appropriate redirections based on their rights

    // If trying to access dashboard but don't have rights
    if (requiredRights.includes("Status Dashboard")) {
      // Check if user can access assessment history
      if (
        userRights.includes("Assessment Requests") &&
        (userSubRoles.includes("VQA Manager") ||
          userSubRoles.includes("Vendor Admin"))
      ) {
        return <Navigate to="/Provider/Assessment/History" replace />;
      }

      // Otherwise check if user can access RFI requests
      if (
        userRights.includes("RFI Requests") ||
        (userRights.includes("Diligent RFI Requests") &&
          (userSubRoles.includes("RFI Manager - Approver") ||
            userSubRoles.includes("RFI Manager - User") ||
            userSubRoles.includes("Vendor Admin")))
      ) {
        return <Navigate to="/RFI/Requests" replace />;
      }
    }

    // For other cases like accessing Documents
    if (requiredRights.includes("Dashboard")) {
      if (userRights.includes("Documents")) {
        return <Navigate to="/Documents" replace />;
      }
    }

    if (requiredRights.includes("Library Requests")) {
      if (
        userRights.includes("Assessment Requests") &&
        (userSubRoles.includes("VQA Manager") ||
          userSubRoles.includes("Vendor Admin"))
      ) {
        return <Navigate to="/Provider/Assessment/History" replace />;
      }
      return <Navigate to="/" replace />;
    }

    return <Navigate to="/" replace />;
  }

  // User has the required permissions
  return children;
};

/**
 * AppRoutes - Main routing component with optimized role switching
 *
 * Key optimizations:
 * - Uses stableRole (debounced) instead of raw role to prevent flicker
 * - Shows LoadingModal during UI freeze (role switch)
 * - Never returns null/empty - always shows controlled loader
 * - Handles Clerk metadata refresh gracefully
 */
const AppRoutes = () => {
  const { isSignedIn, isLoaded } = useAuth();
  const location = useLocation();
  const dispatch = useDispatch();
  const userRole = useUserRole(); // Now returns stableRole with debouncing
  const { user, isLoaded: isUserLoaded } = useUser();
  const isPasswordEnabled = user?.passwordEnabled;
  const freeze = useSelector(selectUIFreeze);
  const prevRoleRef = useRef(userRole);

  // Detect role switch and trigger freeze
  useEffect(() => {
    if (prevRoleRef.current && userRole && prevRoleRef.current !== userRole) {
      // Role is changing - start freeze
      dispatch(startRoleSwitch());

      // End freeze after 150ms (fast transition)
      const timer = setTimeout(() => {
        dispatch(endRoleSwitch());
      }, 150);

      return () => clearTimeout(timer);
    }
    prevRoleRef.current = userRole;
  }, [userRole, dispatch]);

  const {
    data: isFirstTimeSignIn,
    isLoading: isFirstTimeLoading,
    refetch: refetchFirstTimeSignIn,
  } = useGetFirstTimeSignIn(isSignedIn, userRole);

  useEffect(() => {
    if (!isSignedIn) {
      // Don't store sign-in or set-password pages as redirect targets
      const pathsToExclude = ["/sign-in", "/set-password"];
      if (!pathsToExclude.includes(location.pathname)) {
        localStorage.setItem("redirectAfterSignIn", location.pathname);
      }
      localStorage.removeItem("hypercomplyauth");
    } else {
      // User is signed in - clear the redirect path to prevent repeated redirects
      // This ensures the redirect only happens once after login
      localStorage.removeItem("redirectAfterSignIn");
    }
  }, [isSignedIn, location.pathname]);

  // Ensure we never redirect to sign-in page after login - always default to "/"
  const rawRedirectPath = localStorage.getItem("redirectAfterSignIn");
  const storedRedirectPath = rawRedirectPath && rawRedirectPath !== "/sign-in" ? rawRedirectPath : "/";

  // CRITICAL: Show loading modal during UI freeze (role switch)
  // This prevents blank screen during Sponsor ↔ Vendor role switch
  if (freeze) {
    return <LoadingModal open={true} />;
  }

  if (!isLoaded || !isUserLoaded) {
    return <LoadingModal open={true} />;
  }

  // Special case for forgot password - don't restrict access
  if (location.pathname === "/set-password") {
    return (
      <Routes>
        <Route
          path="/set-password"
          element={
            <CenteredContainer>
              <ResetPasswordPage />
            </CenteredContainer>
          }
        />
      </Routes>
    );
  }

  if (isSignedIn && !isPasswordEnabled) {
    return <Navigate to="/set-password" replace />;
  }

  // Use stableRole - it will fallback to localStorage if undefined
  // This prevents blank screen during Clerk metadata refresh

  // CRITICAL: If user is signed in but role is still loading, show loading modal
  // This prevents showing SignIn again during the transition period after login
  if (isSignedIn && !userRole) {
    return <LoadingModal open={true} />;
  }

  // Show SignIn page only when NOT signed in and role is not available
  if (!isLoaded || !userRole) {
    return (
      <CenteredContainer>
        <SignIn
          appearance={{
            layout: {
              logoImageUrl: "/diligent-2025-branding-2.png", // ✅ works now
              logoPlacement: "inside",
            },
            elements: {
              logoImage: "w-40 h-auto mx-auto", // optional: make it larger + centered
              alternativeMethods: "hidden", // hides the "Or, sign in with another method"
              alternativeMethodsBlock: "hidden", // hides the "Or, sign in with another method"
              footer: "hidden", // hides the extra links
              alternativeMethodsBlockButtons: "hidden",
            },
          }}
          fallbackRedirectUrl={storedRedirectPath}
        />
      </CenteredContainer>
    );
  }

  if (isSignedIn && isPasswordEnabled === true) {
    if (userRole === "Sponsor User" || userRole === "Sponsor Master User") {
      return (
        <MfaEnforcedRoute variant="sponsor">
          <Routes>
            <Route
              path="/mfa/setup"
              element={
                <CenteredContainer>
                  <MfaSetupPage />
                </CenteredContainer>
              }
            />
            {/* Base route - accessible to everyone */}
          <Route
            path="/"
            element={
              <ProtectedRoute
                requiredRights={["Status Dashboard"]}
                requiredRoles={["Sponsor Admin", "RFI Manager", "VQA Manager"]}
              >
                <Layout>
                  <StatusDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* Clinical Indications Dashboard route */}
          <Route
            path="/TrialExperience"
            element={
              <ProtectedRoute
                requiredRights={["Trial Experience Dashboard"]}
                requiredRoles={["Sponsor Admin", "RFI Manager", "VQA Manager"]}
              >
                <Layout>
                  <ClinicalDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/Providers"
            element={
              <ProtectedRoute
                requiredRights={["Vendor Discovery"]}
                requiredRoles={["Sponsor Admin", "RFI Manager", "VQA Manager"]}
                allowBackendAdminViewAccess
              >
                <Layout>
                  <Discovery />
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* Sponsor authors their own custom risk models */}
          <Route
            path="/Sponsor/RiskModels"
            element={
              <ProtectedRoute
                requiredRoles={["Sponsor Admin", "RFI Manager", "VQA Manager"]}
              >
                <Layout>
                  <SponsorRiskModels />
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* <Route
            path="/Provider/Compare"
            element={
              <ProtectedRoute
                requiredRights={["Vendor Discovery"]}
                requiredRoles={["Sponsor Admin", "RFI Manager", "VQA Manager"]}
              >
                <Layout>
                  <ProviderCompare />
                </Layout>
              </ProtectedRoute>
            }
          /> */}

          {/* Protected routes with rights/roles requirements */}
          <Route
            path="/documents/RFI"
            element={
              <ProtectedRoute
                requiredRights={["View Released RFIs"]}
                requiredRoles={["Sponsor Admin", "RFI Manager"]}
              >
                <Layout>
                  <RFILibrary />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/Documents/Audit"
            element={
              <ProtectedRoute
                requiredRights={["View Audit Reports"]}
                requiredRoles={["Sponsor Admin", "VQA Manager"]}
              >
                <Layout>
                  <VqaLibrary />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/Assessment/Create"
            element={
              <ProtectedRoute
                requiredRights={["Request/Manage New Assessments"]}
                requiredRoles={["Sponsor Admin", "VQA Manager"]}
                blockForAdminUsers
              >
                <Layout>
                  <RequestNewAssessment />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/Assessment/Calendar"
            element={
              <ProtectedRoute
                requiredRights={["Join Scheduled Assessments"]}
                requiredRoles={["Sponsor Admin", "VQA Manager"]}
                allowBackendAdminViewAccess
              >
                <Layout>
                  <AssessmentCalendar />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/Documents/Audit"
            element={
              <ProtectedRoute
                requiredRights={["Join Scheduled Assessments"]}
                requiredRoles={["Sponsor Admin", "VQA Manager"]}
              >
                <Layout>
                  <VqaLibrary />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Routes that require specific roles */}
          <Route
            path="/RequestAssesmentList"
            element={
              <ProtectedRoute
                requiredRoles={["VQA Manager", "Sponsor Admin"]}
                requiredRights={["View My Assessment"]}
              >
                <Layout>
                  <RequestAssessmentListGrid />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Routes that can be accessed by multiple roles or rights */}
          <Route
            path="/Assessment/History"
            element={
              <ProtectedRoute
                requiredRights={["View My Assessment"]}
                requiredRoles={["VQA Manager", "Sponsor Admin"]}
              >
                <Layout>
                  <SponsorAssessmentGrid />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Continue defining other routes with appropriate permissions... */}

          <Route
            path="/provider/:id/overview"
            element={
              <Layout>
                <ProviderDetails />
              </Layout>
            }
          />
          <Route
            path="/export-history"
            element={
              <Layout>
                <HistoryTable />
              </Layout>
            }
          />

          <Route
            path="/poc-RFI-form"
            element={
              <Layout>
                <RFIUploadPage />
              </Layout>
            }
          />
          <Route
            path="/poc-chatBot"
            element={
              <Layout>
                <PocChatBot />
              </Layout>
            }
          />
          <Route
            path="/RequestAssesment/:id/Details"
            element={
              <Layout>
                <AssesmentRequestDetails />
              </Layout>
            }
          />
          <Route
            path="/Documents"
            element={
              <ProtectedRoute
                requiredRights={["Document Library"]}
                allowBackendAdminViewAccess
              >
                <Layout>
                  <DocumentLibrary />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/Poc"
            element={
              <Layout>
                <Poc />
              </Layout>
            }
          />
          <Route
            path="/MyDocuments"
            element={
              <ProtectedRoute requiredRights={["Document Library"]}>
                <Layout>
                  <SponsorVQAGrid />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path={LIBRARY_REQUESTS_EMAIL_PATHS.provider}
            element={<LibraryRequestsEmailLinkRedirect />}
          />
          <Route
            path={LIBRARY_REQUESTS_EMAIL_PATHS.backend}
            element={<LibraryRequestsEmailLinkRedirect />}
          />

          <Route
            path="/RFI/History"
            element={
              <ProtectedRoute
                requiredRights={["View Requested RFIs"]}
                requiredRoles={["RFI Manager", "Sponsor Admin"]}
              >
                <Layout>
                  <MyRFIs />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/Assessment/:id/Details"
            element={
              <ProtectedRoute
                requiredRights={["View My Assessment"]}
                requiredRoles={["VQA Manager", "Sponsor Admin"]}
              >
                <Layout>
                  <SponsorDetailAssessment />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/Assessment/Calendar"
            element={
              <ProtectedRoute
                requiredRights={["View My Assessment"]}
                requiredRoles={["VQA Manager", "Sponsor Admin"]}
                allowBackendAdminViewAccess
              >
                <Layout>
                  <AssessmentCalendar />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/Sponsor/Users/Me"
            element={
              <Layout>
                <ManageSponsorUser />
              </Layout>
            }
          />
          <Route
            path="/joinVqa/:id/Details"
            element={
              <ProtectedRoute
                requiredRights={["Join Scheduled Assessments"]}
                requiredRoles={["VQA Manager", "Sponsor Admin"]}
              >
                <Layout>
                  <JoinvqaDetailAssessment />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path={`/Backend/AssementParticipation/:id/List`}
            element={
              <Layout>
                <ManageParticipantsList />
              </Layout>
            }
          />
          <Route
            path="/JoinRequest/:id/Details"
            element={
              <ProtectedRoute
                requiredRights={["Join Scheduled Assessments"]}
                requiredRoles={["VQA Manager", "Sponsor Admin"]}
              >
                <Layout>
                  <JoinvqaRequestDetailAssessment />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/Sponsor/Team/Manage"
            element={
              <ProtectedRoute
                requiredRights={["Manage Team"]}
                requiredRoles={["Sponsor Admin"]}
              >
                <Layout>
                  <VendorTeamManagement />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/Sponsor/AddTeamMember"
            element={
              <ProtectedRoute
                requiredRights={["Manage Team"]}
                requiredRoles={["Sponsor Admin"]}
              >
                <Layout>
                  <AddTeamMember />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/RFI/Create"
            element={
              <ProtectedRoute
                requiredRights={["Request RFIs"]}
                requiredRoles={["Sponsor Admin", "RFI Manager"]}
                blockForAdminUsers
              >
                <Layout>
                  <RequestRFis />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/RFI/:id/Details"
            element={
              <ProtectedRoute
                requiredRights={["Request RFIs"]}
                requiredRoles={["Sponsor Admin", "RFI Manager"]}
                allowMyVendorsViewAccess
              >
                <Layout>
                  <RFIDetails />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/Provider/Create"
            element={
              <ProtectedRoute
                requiredRights={["Request RFIs", "Request Audit Reports"]}
                requiredRoles={[
                  "Sponsor Admin",
                  "RFI Manager",
                  "VQA Manager",
                ]}
                blockForAdminUsers
              >
                <Layout>
                  <NewRequestVendor />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/Vendors"
            element={
              <ProtectedRoute requiredRights={["My Vendors"]}>
                <Layout>
                  {NEW_MY_VENDORS_UI_ENABLED ? <MyVendorsPage /> : <Vendors />}
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/Vendors/lists/:listId"
            element={
              <ProtectedRoute requiredRights={["My Vendors"]}>
                <Layout>
                  {NEW_MY_VENDORS_UI_ENABLED ? <VendorListDetailPage /> : <Vendors />}
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/MyVendors"
            element={
              <ProtectedRoute requiredRights={["My Vendors"]}>
                <Layout>
                  {NEW_MY_VENDORS_UI_ENABLED ? (
                    <MyVendorsLegacyRouteRedirect />
                  ) : (
                    <Vendors />
                  )}
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/RFI/Builder"
            element={
              <ProtectedRoute
                requiredRights={["RFI Template Builder"]}
                requiredRoles={["Sponsor Admin", "RFI Manager"]}
              >
                <Layout>
                  <RFITemplateBuilder />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="hypercomply-callback"
            element={<HypercomplyCallback />}
          />
          <Route
            path="sso-bridge"
            element={<SSOBridge />}
          />

          <Route
            path="*"
            element={
              <Layout>
                <Page404 />
              </Layout>
            }
          />
        </Routes>
        </MfaEnforcedRoute>
      );
    } else if (
      userRole.toLowerCase() === "provider user" ||
      userRole.toLowerCase() === "provider master user"
    ) {
      // If first time sign in, only allow /terms
      if (isFirstTimeLoading) {
        return <LoadingModal open={true} />;
      }

      if (isFirstTimeSignIn === false) {
        return (
          <MfaEnforcedRoute variant="provider">
            <Routes>
              <Route
                path="/mfa/setup"
                element={
                  <CenteredContainer>
                    <MfaSetupPage />
                  </CenteredContainer>
                }
              />
              <Route
                path="/terms"
                element={
                  <Layout>
                    <TermsAndConditionsPage refetch={refetchFirstTimeSignIn} />
                  </Layout>
                }
              />
              <Route
                path={LIBRARY_REQUESTS_EMAIL_PATHS.provider}
                element={<LibraryRequestsEmailLinkRedirect />}
              />
              <Route
                path={LIBRARY_REQUESTS_EMAIL_PATHS.backend}
                element={<LibraryRequestsEmailLinkRedirect />}
              />
              {/* Redirect all other routes to /terms */}
              <Route path="*" element={<Navigate to="/terms" replace />} />
            </Routes>
          </MfaEnforcedRoute>
        );
      }

      // Not first time, allow normal vendor routes
      return (
        <MfaEnforcedRoute variant="provider">
          <Routes>
            <Route
              path="/mfa/setup"
              element={
                <CenteredContainer>
                  <MfaSetupPage />
                </CenteredContainer>
              }
            />
            <Route
              path="/terms"
              element={
                <Layout>
                  <TermsAndConditionsPage />
                </Layout>
              }
            />
            <Route
              path="hypercomply-callback"
              element={<HypercomplyCallback />}
            />
          <Route
            path="sso-bridge"
            element={<SSOBridge />}
          />
          <Route
            path="/"
            element={
              <ProtectedRouteVendor
                requiredRights={["Status Dashboard"]}
                requiredRoles={[
                  "VQA Manager",
                  "RFI Manager - Approver",
                  "RFI Manager - User",
                  "Vendor Admin",
                ]}
              >
                <Layout>
                  <ProviderStatusDashboard />
                </Layout>
              </ProtectedRouteVendor>
            }
          />
          <Route
            path="/Provider/Assessment/History"
            element={
              <ProtectedRouteVendor
                requiredRights={["Assessment Requests"]}
                requiredRoles={["VQA Manager", "Vendor Admin"]}
              >
                <Layout>
                  <ProviderAssessmentGrid />
                </Layout>
              </ProtectedRouteVendor>
            }
          />
          <Route
            path="/RFI/Requests"
            element={
              <ProtectedRouteVendor
                requiredRights={["RFI Requests", "Diligent RFI Requests"]}
                requiredRoles={[
                  "RFI Manager - Approver",
                  "RFI Manager - User",
                  "Vendor Admin",
                ]}
              >
                <Layout>
                  <ProviderRequestedRfis />
                </Layout>
              </ProtectedRouteVendor>
            }
          />
          <Route
            path="/Assessment/Requests"
            element={
              <ProtectedRouteVendor
                requiredRights={["Library Requests"]}
                requiredRoles={["VQA Manager", "Vendor Admin"]}
              >
                <Layout>
                  <ProviderVQAGrid />
                </Layout>
              </ProtectedRouteVendor>
            }
          />
          <Route
            path={LIBRARY_REQUESTS_EMAIL_PATHS.backend}
            element={<LibraryRequestsEmailLinkRedirect />}
          />
          <Route
            path="/Knowledgebase"
            element={
              <ProtectedRouteVendor
                requiredRights={["Knowledge Base"]}
                requiredRoles={[
                  "RFI Manager - Approver",
                  "RFI Manager - User",
                  "Vendor Admin",
                ]}
              >
                <Layout>
                  <KnowledgeBase />
                </Layout>
              </ProtectedRouteVendor>
            }
          />
          <Route
            path="/Questionnaires"
            element={
              <ProtectedRouteVendor
                requiredRights={["Questionnaire", "All Questionnaires"]}
                requiredRoles={[
                  "RFI Manager - Approver",
                  "RFI Manager - User",
                  "Vendor Admin",
                ]}
              >
                <Layout>
                  <Questionnaire />
                </Layout>
              </ProtectedRouteVendor>
            }
          />
          <Route
            path="/Documents"
            element={
              <ProtectedRouteVendor
                requiredRights={["Documents"]}
                requiredRoles={[
                  "RFI Manager - Approver",
                  "RFI Manager - User",
                  "Vendor Admin",
                ]}
              >
                <Layout>
                  <Documents />
                </Layout>
              </ProtectedRouteVendor>
            }
          />
          <Route
            path="/Segmentation"
            element={
              <ProtectedRouteVendor
                requiredRights={["Segmentation"]}
                requiredRoles={[
                  "RFI Manager - Approver",
                  "RFI Manager - User",
                  "Vendor Admin",
                ]}
              >
                <Layout>
                  <Segmentation />
                </Layout>
              </ProtectedRouteVendor>
            }
          />
          <Route
            path="/Vendors"
            element={
              <ProtectedRouteVendor
                requiredRights={["Vendors"]}
                requiredRoles={[
                  "RFI Manager - Approver",
                  "RFI Manager - User",
                  "Sponsor Admin",
                  "Sponsor User",
                  "Sponsor Master User",
                ]}
              >
                <Layout>
                  <Vendors />
                </Layout>
              </ProtectedRouteVendor>
            }
          />
          <Route
            path="/DataRooms"
            element={
              <ProtectedRouteVendor
                requiredRights={["Data Rooms"]}
                requiredRoles={[
                  "RFI Manager - Approver",
                  "RFI Manager - User",
                  "Vendor Admin",
                ]}
              >
                <Layout>
                  <DataRooms />
                </Layout>
              </ProtectedRouteVendor>
            }
          />
          <Route
            path="/TrustPages"
            element={
              <ProtectedRouteVendor
                requiredRights={["Trust Pages"]}
                requiredRoles={[
                  "RFI Manager - Approver",
                  "RFI Manager - User",
                  "Vendor Admin",
                ]}
              >
                <Layout>
                  <TrustPages />
                </Layout>
              </ProtectedRouteVendor>
            }
          />
          <Route
            path="/user/team/profile/edit"
            element={
              <ProtectedRouteVendor
                // requiredRights={["User Profile"]}
                requiredRoles={["Vendor Admin"]}
              >
                <Layout>
                  <VendorTeamManagement />
                </Layout>
              </ProtectedRouteVendor>
            }
          />
          <Route
            path="/Provider/addTeamMember"
            element={
              <ProtectedRouteVendor
                requiredRoles={["Vendor Admin"]}
              >
                <Layout>
                  <AddProviderUser />
                </Layout>
              </ProtectedRouteVendor>
            }
          />
          <Route
            path="/RFI/:id/Details"
            element={
              <ProtectedRouteVendor
                requiredRights={["RFI Requests", "Diligent RFI Requests"]}
                requiredRoles={[
                  "Vendor Admin",
                  "RFI Manager - Approver",
                  "RFI Manager - User",
                ]}
              >
                <Layout>
                  <RFIDetails />
                </Layout>
              </ProtectedRouteVendor>
            }
          />
          <Route
            path="/Provider/Users/Me"
            element={
              <Layout>
                <ManageProviderUser />
              </Layout>
            }
          />
          <Route
            path="*"
            element={
              <Layout>
                <Page404 />
              </Layout>
            }
          />
        </Routes>
        </MfaEnforcedRoute>
      );
    } else if (
      userRole === "Backend User" ||
      userRole === "Backend Master User"
    ) {
      return (
        <MfaEnforcedRoute variant="backend">
          <Routes>
            <Route
              path="/mfa/setup"
              element={
                <CenteredContainer>
                  <MfaSetupPage />
                </CenteredContainer>
              }
            />
            <Route
              path="/"
              element={
                <Layout>
                  <ProviderStatusDashboard />
                </Layout>
              }
            />
          <Route
            path="/Providers"
            element={
              <ProtectedRoute allowBackendAdminViewAccess>
                <Layout>
                  <Discovery />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/Documents"
            element={
              <ProtectedRoute allowBackendAdminViewAccess>
                <Layout>
                  <DocumentLibrary />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/provider/:id/overview"
            element={
              <Layout>
                <ProviderDetails />
              </Layout>
            }
          />
          <Route
            path="/Provider/:id/overview"
            element={
              <Layout>
                <ProviderDetails />
              </Layout>
            }
          />
          <Route
            path="/RFI/:id/Details"
            element={
              <Layout>
                <RFIDetails />
              </Layout>
            }
          />
          {/* Admin vendor-details CTAs → same create pages sponsors use */}
          <Route
            path="/RFI/Create"
            element={
              <Layout>
                <RequestRFis />
              </Layout>
            }
          />
          <Route
            path="/Assessment/Create"
            element={
              <Layout>
                <RequestNewAssessment />
              </Layout>
            }
          />
          <Route
            path="/Assessment/Calendar"
            element={
              <ProtectedRoute allowBackendAdminViewAccess>
                <Layout>
                  <AssessmentCalendar />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/Request/Library"
            element={
              <Layout>
                <BackendUserAssessment />
              </Layout>
            }
          />
          <Route
            path={LIBRARY_REQUESTS_EMAIL_PATHS.provider}
            element={<LibraryRequestsEmailLinkRedirect />}
          />
          <Route
            path="/Backend/Assessment/History"
            element={
              <Layout>
                <AdminAssessmentGrid />
              </Layout>
            }
          />
          <Route
            path="/Backend/Assessment/Request/History"
            element={
              <Layout>
                <AdminAssessmentRequestGrid />
              </Layout>
            }
          />
          <Route
            path="/Backend/Assessment/:id/Manage"
            element={
              <Layout>
                <BackendManageAssessment />
              </Layout>
            }
          />
          <Route
            path="/Assessments/Admin/Request/:id"
            element={
              <Layout>
                <BackendRequestDetailPage />
              </Layout>
            }
          />
          <Route
            path="/Backend/Assessment/Auditor/:id/Detail"
            element={
              <Layout>
                <AuditorDetail />
              </Layout>
            }
          />
          <Route
            path="/Backend/Users/Vendors"
            element={
              <Layout>
                <UsersList />
              </Layout>
            }
          />
          <Route
            path="/Backend/Users/Sponsors/AddUser"
            element={
              <Layout>
                <AddSponsorUsers />
              </Layout>
            }
          />
          <Route
            path={`/Backend/Users/Vendors/:id`}
            element={
              <Layout>
                <Providers />
              </Layout>
            }
          />
          <Route
            path="/Backend/Users/AddVendorAccount"
            element={
              <Layout>
                {/* <AddVendorAccount /> */}
                <CreateVendorAccountPage />
              </Layout>
            }
          />
          <Route
            path="/Backend/Users/AddVendorAccount/:id"
            element={
              <Layout>
                <VendorTeamManagement type="vendor" />
              </Layout>
            }
          />
          <Route
            path="/provider/:id/overview"
            element={
              <Layout>
                <ProviderDetails />
              </Layout>
            }
          />
          <Route
            path="/Backend/Users/Sponsors/list/:id"
            element={
              <Layout>
                <SponsorsUsersList />
              </Layout>
            }
          />
          <Route
            path="/Backend/Users/Sponsors"
            element={
              <Layout>
                <SponsorUserGrid />
              </Layout>
            }
          />
          <Route
            path="/Backend/Users/AddSponsors"
            element={
              <Layout>
                <SponsorForm />
              </Layout>
            }
          />
          <Route
            path="/Backend/Users/AddSponsors/:id"
            element={
              <Layout>
                <VendorTeamManagement type="sponsor" />
              </Layout>
            }
          />
          <Route
            path="/Backend/Users/AddSponsorAccount"
            element={
              <Layout>
                <AddSponsorAccount />
              </Layout>
            }
          />
          <Route
            path="/Backend/Users/AddSponsors"
            element={
              <Layout>
                <SponsorForm />
              </Layout>
            }
          />
          <Route
            path="/Backend/Users/ManageSponsor/:id"
            element={
              <Layout>
                <ManageSponsor />
              </Layout>
            }
          />
          <Route
            path="/Backend/Users/Me"
            element={
              <Layout>
                <MyProfile />
              </Layout>
            }
          />
          <Route
            path="/Backend/Users/SponsorMasterUsers"
            element={
              <Layout>
                <SponsorMasterUserList />
              </Layout>
            }
          />
          <Route
            path="/Backend/Users/AddSponsorMasterUser"
            element={
              <Layout>
                <AddSponsorMasterUser />
              </Layout>
            }
          />
          <Route
            path="/Backend/Users/SponsorMasterUsers/:id/manage"
            element={
              <Layout>
                <ManageSponsorMasterUser />
              </Layout>
            }
          />
          <Route
            path="/Backend/Users/AddProviderUser"
            element={
              <Layout>
                <AddProviderUser />
              </Layout>
            }
          />
          <Route
            path="/Backend/Users/Vendors"
            element={
              <Layout>
                <UsersList />
              </Layout>
            }
          />
          <Route
            path={`/Backend/Users/Vendors/:id`}
            element={
              <Layout>
                <Providers />
              </Layout>
            }
          />

          <Route
            path="/Backend/Users/AddSponsors"
            element={
              <Layout>
                <SponsorForm />
              </Layout>
            }
          />

          <Route
            path="/Backend/Users/AddSponsorAccount"
            element={
              <Layout>
                <AddSponsorAccount />
              </Layout>
            }
          />
          <Route
            path="/Backend/Users/AddSponsors"
            element={
              <Layout>
                <SponsorForm />
              </Layout>
            }
          />
          <Route
            path="/Backend/Users/ManageSponsor/:id"
            element={
              <Layout>
                <ManageSponsor />
              </Layout>
            }
          />
          <Route
            path="/Backend/Users/BackendUsers"
            element={
              <Layout>
                <BackendUsersList />
              </Layout>
            }
          />

          <Route
            path="/Backend/Users/AddBackendUser"
            element={
              <Layout>
                <AddBackendUserAccount />
              </Layout>
            }
          />
          <Route
            path="/Backend/AssementParticipation/Details"
            element={
              <Layout>
                <JoinvqaDetailAssessment />
              </Layout>
            }
          />
          <Route
            path="/Backend/AssementParticipation/:id/List"
            element={
              <Layout>
                <ManageParticipantsList />
              </Layout>
            }
          />
          <Route
            path="/Backend/JoinRequests"
            element={
              <Layout>
                <JoinRequestsQueue />
              </Layout>
            }
          />

          <Route
            path="/Backend/Users/BackendUsers/:id/manage"
            element={
              <Layout>
                <ManageBackendUser />
              </Layout>
            }
          />
          <Route
            path="/Backend/Users/AddBackendUser"
            element={
              <Layout>
                <AddBackendUserAccount />
              </Layout>
            }
          />
          <Route
            path="/Backend/AssementParticipation/Details"
            element={
              <Layout>
                <JoinvqaDetailAssessment />
              </Layout>
            }
          />
          <Route
            path="/Backend/AssementParticipation/:id/List"
            element={
              <Layout>
                <ManageParticipantsList />
              </Layout>
            }
          />
          <Route
            path="/JoinRequest/:id/Details"
            element={
              <Layout>
                <JoinvqaRequestDetailAssessment />
              </Layout>
            }
          />
          <Route
            path="/Backend/Users/Me"
            element={
              <Layout>
                <MyProfile />
              </Layout>
            }
          />
          <Route
            path="/Backend/Users/SponsorMasterUsers"
            element={
              <Layout>
                <SponsorMasterUserList />
              </Layout>
            }
          />
          <Route
            path="/Backend/Users/AddSponsorMasterUser"
            element={
              <Layout>
                <AddSponsorMasterUser />
              </Layout>
            }
          />
          <Route
            path="/Backend/Users/SponsorMasterUsers/:id/manage"
            element={
              <Layout>
                <ManageSponsorMasterUser />
              </Layout>
            }
          />

          <Route
            path="/Backend/Users/Me"
            element={
              <Layout>
                <MyProfile />
              </Layout>
            }
          />
          <Route
            path="/Backend/Users/SponsorMasterUsers"
            element={
              <Layout>
                <SponsorMasterUserList />
              </Layout>
            }
          />
          <Route
            path="/Backend/Users/AddSponsorMasterUser"
            element={
              <Layout>
                <AddSponsorMasterUser />
              </Layout>
            }
          />
          <Route
            path="/Backend/Users/SponsorMasterUsers/:id/manage"
            element={
              <Layout>
                <ManageSponsorMasterUser />
              </Layout>
            }
          />

          <Route
            path="/Backend/Rfis"
            element={
              <Layout>
                <BackendRfis />
              </Layout>
            }
          />
          <Route
            path="/Backend/RiskModels"
            element={
              <Layout>
                <AdminRiskModels />
              </Layout>
            }
          />
          <Route
            path="/Backend/RiskModels/Diligent"
            element={
              <Layout>
                <AdminDiligentRiskModel />
              </Layout>
            }
          />
          <Route
            path="/RFI/:id/Details"
            element={
              <Layout>
                <RFIDetails />
              </Layout>
            }
          />
          <Route
            path="*"
            element={
              <Layout>
                <Page404 />
              </Layout>
            }
          />
        </Routes>
        </MfaEnforcedRoute>
      );
    } else {
      // Fallback for other user roles
      return <ComingSoon />;
    }
  }

  // Rest of your existing return statements...
  return (
    <Routes>
      <Route
        path="/set-password"
        element={
          <CenteredContainer>
            <ResetPasswordPage />
          </CenteredContainer>
        }
      />
      <Route
        path="/set-password"
        element={
          <CenteredContainer>
            <ResetPasswordPage />
          </CenteredContainer>
        }
      />
      <Route
        path="/sign-in"
        element={
          <CenteredContainer>
            <SignIn
              appearance={{
                layout: {
                  logoImageUrl: "/diligent-2025-branding-2.png", // ✅ works now
                  logoPlacement: "inside",
                },
                elements: {
                  logoImage: "w-40 h-auto mx-auto", // optional: make it larger + centered
                  alternativeMethods: "hidden", // hides the "Or, sign in with another method"
                  alternativeMethodsBlockButtons: "hidden",
                  alternativeMethodsBlock: "hidden", // hides the "Or, sign in with another method"
                  footer: "hidden", // hides the extra links
                },
              }}
              fallbackRedirectUrl={storedRedirectPath}
            />
          </CenteredContainer>
        }
      />
      {/* Catch-all: Show SignIn for unauthenticated users */}
      <Route
        path="*"
        element={
          <CenteredContainer>
            <SignIn
              appearance={{
                layout: {
                  logoImageUrl: "/diligent-2025-branding-2.png",
                  logoPlacement: "inside",
                },
                elements: {
                  logoImage: "w-40 h-auto mx-auto",
                  alternativeMethods: "hidden",
                  alternativeMethodsBlockButtons: "hidden",
                  alternativeMethodsBlock: "hidden",
                  footer: "hidden",
                },
              }}
              fallbackRedirectUrl="/"
            />
          </CenteredContainer>
        }
      />
    </Routes>
  );
};

export default AppRoutes;
